import React, { useState } from "react";
import {
  TextInput,
  View,
  StyleSheet,
  TextStyle,
  ViewStyle,
  StyleProp,
  KeyboardTypeOptions,
} from "react-native";
import { Text } from "./Text";
import { radius, spacing, fonts, fontSizes } from "~/styles/tokens";
import { useTheme } from "~/styles/theme";

interface InputProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChangeText: (text: string) => void;
  secureTextEntry?: boolean;
  keyboardType?: KeyboardTypeOptions;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  error?: string;
  hint?: string;
  disabled?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  style?: StyleProp<ViewStyle>;
  autoComplete?: "off" | "email" | "password" | "new-password" | "username";
}

export const Input: React.FC<InputProps> = ({
  label,
  placeholder,
  value,
  onChangeText,
  secureTextEntry = false,
  keyboardType = "default",
  autoCapitalize = "sentences",
  error,
  hint,
  disabled = false,
  multiline = false,
  numberOfLines,
  style,
  autoComplete = "off",
}) => {
  const { colors } = useTheme();
  const [focused, setFocused] = useState(false);

  return (
    <View style={[styles.container, style]}>
      {label ? (
        <Text variant="label" color={colors.heading} style={styles.label}>
          {label}
        </Text>
      ) : null}
      <TextInput
        style={[
          styles.input,
          {
            backgroundColor: colors.surface,
            borderColor: colors.border,
            color: colors.foreground,
          },
          focused && { borderColor: colors.focusRing },
          error ? { borderColor: colors.danger } : null,
          disabled && styles.inputDisabled,
          multiline && styles.inputMultiline,
        ]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.muted}
        secureTextEntry={secureTextEntry}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        editable={!disabled}
        multiline={multiline}
        numberOfLines={numberOfLines}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete={autoComplete}
        accessibilityLabel={label ?? placeholder}
        accessibilityHint={hint}
        accessibilityState={{ disabled }}
      />
      {error ? (
        <Text variant="caption" color={colors.danger} style={styles.message}>
          {error}
        </Text>
      ) : null}
      {hint && !error ? (
        <Text variant="caption" style={styles.message}>
          {hint}
        </Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: spacing.sm,
  },
  label: {
    marginBottom: spacing.xs,
  },
  input: {
    borderWidth: 1.5,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 3,
    fontFamily: fonts.body,
    fontSize: fontSizes.md,
    minHeight: 48,
  } as TextStyle,
  inputDisabled: {
    opacity: 0.5,
  },
  inputMultiline: {
    minHeight: 100,
    textAlignVertical: "top",
    paddingTop: spacing.sm,
  },
  message: {
    marginTop: spacing.xs,
  },
});
